import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";

const AboutSection = () => {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  const foundingYear = 1999; // ⏪ Change this to your company's founding year
  const currentYear = new Date().getFullYear();
  const yearsOfExperience = currentYear - foundingYear;

  // Observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target); // ek hi baar trigger ho
        }
      },
      { threshold: 0.2 } // 20% section visible hone par trigger
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  // CSS animations
  const animationStyle = `
    @keyframes fade-in-up {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fade-in-up {
      animation: fade-in-up 1s ease-out forwards;
      opacity: 0;
    }
  `;

  return (
    <div ref={sectionRef} className="bg-gray-50 py-16 lg:py-24  ">
      <style>{animationStyle}</style>
      <div className="containers  px-4 sm:px-6 lg:px-8 ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left Side - Images */}
          <div
            className={`relative order-2 lg:order-1 ${inView ? "animate-fade-in-up" : ""
              }`}
            style={{ animationDelay: "0.2s" }}
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative overflow-hidden rounded-lg shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=80"
                  alt="Business team working"
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-10"></div>
              </div>

              {/* Secondary Image */}
              <div className="absolute -top-4 -right-4 w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 overflow-hidden rounded-lg shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=1000&q=80"
                  alt="VR Technology"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-10"></div>
              </div>

              {/* Decorative Dots */}
              <div className="absolute -top-8 -left-8 grid grid-cols-4 gap-2 opacity-60">
                {[...Array(16)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-blue-500 rounded-full"
                  ></div>
                ))}
              </div>

              {/* Experience Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-full shadow-lg p-6 hidden sm:block">
                <div className="text-center">
                  <div className="text-4xl lg:text-5xl font-bold text-gray-900">
                    {yearsOfExperience}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    Years<br />Experience
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div
            className={`order-1 lg:order-2 ${inView ? "animate-fade-in-up" : ""
              }`}
            style={{ animationDelay: "0.4s" }}
          >
            <div className="space-y-6">
              {/* Section Header */}
              <div className="space-y-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  • Hey! We Dynamic softlink
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  We Always Try to Give <br className="hidden sm:block" />
                  <span className="text-[#1C74BB]">Smart Solution</span> For
                  Business
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  With over  {yearsOfExperience} years of expertise, we blend technology and creativity to deliver smart solutions that help businesses scale effectively
                </p>
              </div>

              {/* Features */}
              <div className="space-y-6 ">
                {/* Cloud Based */}
                <div className="space-y-6 grid lg:grid-cols-2 grid-col-12 lg:m-0  ">
                  {/* 1 - Successful Deliveries */}
                  <div
                    className={`flex items-center space-x-4 ${inView ? "animate-fade-in-up" : ""}`}
                    style={{ animationDelay: "0.2s" }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2l4-4m5 2a9 9 0 11-18 0a9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h5 className="text-lg font-semibold text-gray-900">
                        50+ Successful Product Deliveries
                      </h5>
                    </div>
                  </div>

                  {/* 2 - Certified Developers */}
                  <div
                    className={`flex items-center  space-x-4  ${inView ? "animate-fade-in-up" : ""}`}
                    style={{ animationDelay: "0.4s", margin: '0px' }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h5 className="text-lg font-semibold text-gray-900 m-0">
                        A Dedicated Team of Certified Developers
                      </h5>
                    </div>
                  </div>

                  {/* 3 - Global Mindset */}
                  <div
                    className={`flex items-center space-x-4 ${inView ? "animate-fade-in-up" : ""}`}
                    style={{ animationDelay: "0.6s" }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364-6.364l-1.414 1.414M6.05 17.95l-1.414 1.414M18.364 18.364l-1.414-1.414M6.05 6.05L4.636 4.636"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h5 className="text-lg font-semibold text-gray-900">
                        Trusted Local Expertise, Rooted in Your Community
                      </h5>
                    </div>
                  </div>

                  {/* 4 - Security Standards */}
                  <div
                    className={`flex items-center space-x-4 ${inView ? "animate-fade-in-up" : ""}`}
                    style={{ animationDelay: "0.8s" }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 11c0-1.104.896-2 2-2s2 .896 2 2c0 3.314-2.686 6-6 6s-6-2.686-6-6c0-1.104.896-2 2-2s2 .896 2 2"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h5 className="text-lg font-semibold text-gray-900">
                        Secure, High-Quality Standards
                      </h5>
                    </div>
                  </div>

                  {/* 5 - Agile Delivery */}
                  <div
                    className={`flex items-center space-x-4 ${inView ? "animate-fade-in-up" : ""}`}
                    style={{ animationDelay: "1s" }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m0-4h.01M21 12a9 9 0 11-18 0a9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h5 className="text-lg font-semibold text-gray-900">
                        On-Time, On-Budget Agile Delivery
                      </h5>
                    </div>
                  </div>
                </div>

                {/* Technical Support */}

              </div>

              {/* Bottom Section */}
              <div className="flex justify-between flex-col sm:flex-row sm:items-center sm:space-x-8 space-y-6 sm:space-y-0 pt-6">
                {/* CTA Button */}
                <Link to="/Contact" onClick={() => window.scrollTo(0, 0)} className="button_same  text-white py-[14px] px-[18px] rounded-md font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105">
                  Discover More
                </Link>

                {/* Avatar Section */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg">
                    <img
                      // src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=1000&q=80"
                      src={require('../../../Img/lalit_sir.jpeg')}
                      alt="Lalit Periwal"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h6 className="text-lg font-semibold text-gray-900">
                      Lalit Periwal
                    </h6>
                    <p className="text-gray-600">Managing Director</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End Right Side */}
        </div>
      </div>
    </div>
  );
};

export default AboutSection;
